/**
 * SwimlanesModal - Modal para reorganizar o fluxo em raias (swimlanes)
 *
 * Permite ao usuário selecionar um campo de agrupamento e converter
 * o layout do mapa em raias horizontais organizadas por metadado.
 */
import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Layers, User, Building2 } from 'lucide-react';
import { CardData } from '../types';
import { SwimLaneGroupBy } from '../utils/autoLayoutEngine';

interface SwimlanesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (groupBy: SwimLaneGroupBy) => void;
  cards: CardData[];
}

interface GroupByOption {
  value: SwimLaneGroupBy;
  label: string;
  description: string;
  icon: React.ReactNode;
}

const GROUP_OPTIONS: GroupByOption[] = [
  {
    value: 'quem',
    label: 'Quem',
    description: 'Agrupa pelo campo "Quem" de cada atividade',
    icon: <User size={18} strokeWidth={1.5} />,
  },
  {
    value: 'setor',
    label: 'Departamento',
    description: 'Agrupa pelo departamento da atividade',
    icon: <Building2 size={18} strokeWidth={1.5} />,
  },
];

export const SwimlanesModal: React.FC<SwimlanesModalProps> = ({
  isOpen,
  onClose,
  onApply,
  cards,
}) => {
  const [selectedGroupBy, setSelectedGroupBy] = useState<SwimLaneGroupBy>('quem');

  // Pré-visualizar os grupos detectados para a opção selecionada
  const detectedGroups = useMemo(() => {
    const workCards = cards.filter(c => c.tipo !== 'card_inicial');
    const groups = new Map<string, number>();

    workCards.forEach(card => {
      const value = card[selectedGroupBy] || 'Sem atribuição';
      groups.set(value, (groups.get(value) || 0) + 1);
    });

    return Array.from(groups.entries())
      .sort((a, b) => a[0].localeCompare(b[0], 'pt-BR'))
      .map(([label, count]) => ({ label, count }));
  }, [cards, selectedGroupBy]);

  const totalCards = cards.filter(c => c.tipo !== 'card_inicial').length;

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-[440px] max-h-[80vh] bg-white dark:bg-slate-800 rounded shadow-xl border border-gray-200 dark:border-slate-700 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 dark:border-slate-700">
          <div className="flex items-center gap-2.5">
            <Layers size={18} className="text-blue-600 dark:text-blue-400" />
            <h2 className="text-sm font-semibold text-gray-800 dark:text-slate-100">
              Converter em Raias
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-400 dark:text-slate-500"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 overflow-y-auto flex-1">
          {/* GroupBy selector */}
          <p className="text-xs text-gray-500 dark:text-slate-400 mb-3">
            Selecione o critério de agrupamento para criar as raias:
          </p>

          <div className="space-y-2 mb-5">
            {GROUP_OPTIONS.map(opt => {
              const isActive = selectedGroupBy === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => setSelectedGroupBy(opt.value)}
                  className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-md border text-left transition-colors ${
                    isActive
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 dark:border-blue-500'
                      : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500 hover:bg-gray-50 dark:hover:bg-slate-750'
                  }`}
                >
                  <span className={isActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-slate-500'}>
                    {opt.icon}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className={`text-xs font-medium ${
                      isActive ? 'text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-slate-200'
                    }`}>
                      {opt.label}
                    </div>
                    <div className="text-[10px] text-gray-400 dark:text-slate-500 mt-0.5">
                      {opt.description}
                    </div>
                  </div>
                  {isActive && (
                    <div className="w-2 h-2 rounded-full bg-blue-500" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Preview of detected groups */}
          <div className="border border-gray-100 dark:border-slate-700 rounded-md p-3 bg-gray-50/50 dark:bg-slate-900/50">
            <p className="text-[10px] font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-2">
              Raias detectadas ({detectedGroups.length})
            </p>
            {detectedGroups.length === 0 ? (
              <p className="text-xs text-gray-400 dark:text-slate-500 italic">
                Nenhum card possui este campo preenchido.
              </p>
            ) : (
              <div className="space-y-1 max-h-[180px] overflow-y-auto">
                {detectedGroups.map(({ label, count }) => (
                  <div
                    key={label}
                    className="flex items-center justify-between px-2.5 py-1.5 rounded text-xs bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700"
                  >
                    <span className="text-gray-700 dark:text-slate-200 font-medium truncate">
                      {label}
                    </span>
                    <span className="text-gray-400 dark:text-slate-500 text-[10px] ml-2 shrink-0">
                      {count} {count === 1 ? 'card' : 'cards'}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-2">
              Total: {totalCards} {totalCards === 1 ? 'atividade' : 'atividades'}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 dark:border-slate-700">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={() => {
              onApply(selectedGroupBy);
              onClose();
            }}
            disabled={detectedGroups.length === 0}
            className="px-4 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded transition-colors"
          >
            Aplicar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
